import type { UseImageEditorResult } from '../../hooks/useImageEditor';

interface BorderPanelProps {
  editor: UseImageEditorResult;
}

const SHAPE_OPTIONS: {
  value: UseImageEditorResult['borderShape'];
  label: string;
}[] = [
  { value: 'rect', label: '無' },
  { value: 'rounded', label: '圓角矩形' },
  { value: 'circle', label: '圓形' },
];

/**
 * 圖片邊線處理：導圓角、形狀遮罩、邊線粗細與顏色。
 * 置於色彩調整之後、畫布尺寸之前。
 */
export function BorderPanel({ editor }: BorderPanelProps) {
  const {
    borderShape,
    borderCornerRadius,
    borderWidth,
    borderColor,
    setBorderShape,
    setBorderCornerRadius,
    setBorderWidth,
    setBorderColor,
  } = editor;

  return (
    <div className="space-y-4">
      <div>
        <span className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
          形狀遮罩
        </span>
        <div className="flex flex-wrap gap-2">
          {SHAPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setBorderShape(opt.value)}
              className={`rounded px-3 py-1.5 text-sm ${
                borderShape === opt.value
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {borderShape === 'rounded' && (
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">
              圓角半徑 (px)
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {borderCornerRadius}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={120}
            step={2}
            value={borderCornerRadius}
            onChange={e => setBorderCornerRadius(Number(e.target.value))}
            className="w-full accent-primary"
          />
        </div>
      )}

      <div>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">
            邊線粗細 (px)
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {borderWidth}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={20}
          step={1}
          value={borderWidth}
          onChange={e => setBorderWidth(Number(e.target.value))}
          className="w-full accent-primary"
        />
      </div>

      <div>
        <span className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
          邊線顏色
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="color"
            value={borderColor}
            onChange={e => setBorderColor(e.target.value)}
            className="h-9 w-14 cursor-pointer rounded border border-gray-300 bg-white p-0 dark:border-gray-600 dark:bg-gray-700"
            aria-label="邊線顏色"
          />
          <input
            type="text"
            value={borderColor}
            onChange={e => setBorderColor(e.target.value)}
            className="w-24 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            placeholder="#000000"
            aria-label="邊線色碼"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        即時反映於上方預覽；按下「輸出並下載」時一併套用至輸出檔。
      </p>
    </div>
  );
}
